import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Gift } from "lucide-react";
import { toast } from "sonner";
import AnimatedLogo from "@/components/AnimatedLogo";

export default function Login() {
  const [params] = useSearchParams();
  const refFromUrl = params.get("ref") || "";
  const [mode, setMode] = useState(refFromUrl ? "signup" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [referralCode, setReferralCode] = useState(refFromUrl);
  const [loading, setLoading] = useState(false);
  const { login, signup } = useAuth();
  const nav = useNavigate();

  useEffect(() => { if (refFromUrl) setReferralCode(refFromUrl); }, [refFromUrl]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await signup(email, password, name || email.split("@")[0], referralCode || undefined);
      toast.success("Welcome!");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auth failed");
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-8 group">
          <AnimatedLogo size={48} replay className="shrink-0" />
          <span className="font-heading text-2xl font-bold gold-gradient-text tracking-tight">AfroRichMind</span>
        </Link>
        <div className="glass-card p-8">
          <h1 className="font-heading text-2xl font-bold mb-2">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
          <p className="text-sm text-[color:var(--text-secondary)] mb-6">Your AI financial coach is one step away.</p>

          <button data-testid="google-login-btn" onClick={googleLogin}
            className="w-full py-3 rounded-xl bg-white text-black font-medium flex items-center justify-center gap-2 hover:bg-gray-100 transition mb-4">
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-4"><div className="flex-1 h-px bg-[rgba(212,175,55,0.15)]" /><span className="text-xs text-[color:var(--text-muted)]">or</span><div className="flex-1 h-px bg-[rgba(212,175,55,0.15)]" /></div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && (
              <input data-testid="auth-name-input" type="text" placeholder="Your name" value={name} onChange={e => setName(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] focus:border-[color:var(--gold)] outline-none transition" />
            )}
            {mode === "signup" && (
              <div className="relative">
                <input data-testid="auth-referral-input" type="text" placeholder="Referral code (optional)" value={referralCode} onChange={e => setReferralCode(e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 pl-10 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] focus:border-[color:var(--gold)] outline-none transition uppercase" />
                <Gift className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[color:var(--gold)]" />
                {refFromUrl && <div className="text-xs text-[color:var(--emerald)] mt-1 ml-1">Friend's code applied — they'll get a free month when you subscribe.</div>}
              </div>
            )}
            <input data-testid="auth-email-input" type="email" placeholder="you@work.com" required value={email} onChange={e => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] focus:border-[color:var(--gold)] outline-none transition" />
            <input data-testid="auth-password-input" type="password" placeholder="Password" required value={password} onChange={e => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] focus:border-[color:var(--gold)] outline-none transition" />
            <button data-testid="auth-submit-btn" type="submit" disabled={loading} className="gold-btn w-full py-3 rounded-xl disabled:opacity-60">
              {loading ? "..." : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <button data-testid="auth-toggle-mode" onClick={() => setMode(mode === "login" ? "signup" : "login")}
            className="w-full text-sm text-[color:var(--text-secondary)] hover:text-[color:var(--gold)] mt-4 transition">
            {mode === "login" ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
}
